<template>
    <ExperienceDialog
        v-if="experienceCreateData?.fullscreenDialog?.component"
        :title="dialogTitle || ''"
        :fullscreen="true"
        :visibleBack="visibleBack"
        :component="experienceCreateData.fullscreenDialog.component"
        @close="handleClose"
        @back="handleBack('fullscreenDialog')"
    >
        <component
            :is="experienceCreateData.fullscreenDialog.component"
            :entryPosition="experienceCreateData.entryPosition"
            :entryItem="experienceCreateData.entryItem"
            :experienceType="experienceCreateData.experienceType"
            :data="experienceCreateData.fullscreenDialog.componentData"
            @update="createDataStore.updateExperienceCreateData"
            @close="handleClose"
            @createWithTemplate="handleCreateWithTemplate"
        ></component>
    </ExperienceDialog>

    <ExperienceDialog
        v-if="experienceCreateData?.dialog?.component"
        :headIcon="createDialogIcon"
        :title="createDialogTitle || ''"
        :visibleBack="experienceCreateData?.dialog?.componentData?.visibleBack"
        :component="experienceCreateData?.dialog.component"
        @close="closeDialog"
        @back="handleBack('dialog')"
    >
        <component
            :is="experienceCreateData.dialog.component"
            :entryPosition="experienceCreateData.entryPosition"
            :entryItem="experienceCreateData.entryItem"
            :experienceType="experienceCreateData.experienceType"
            :data="experienceCreateData.dialog.componentData"
            @update="createDataStore.updateExperienceCreateData"
            @create="handleCreateWithBlank"
            @createWithTemplate="handleCreateWithTemplate"
            @close="handleClose"
        ></component>
    </ExperienceDialog>
</template>

<script lang="ts">
import ExperienceDialog from './ExperienceDialog.vue';
import ExperienceCreateEntry from './ExperienceCreateEntry.vue';
import ExperienceForm from './ExperienceForm.vue';
import ExperienceABTestTypeSelect from './ExperienceABTestTypeSelect.vue';
import ExperienceNonABTestList from './ExperienceNonABTestList.vue';
import ExperienceTemplate from './ExperienceTemplate.vue';
import ExperienceTemplateBlank from '@experience/components/dialog/ExperienceTemplateBlank.vue';
import router from '@router/router';

export default {
    name: 'ExperienceCreate',
    components: {
        ExperienceCreateEntry,
        ExperienceForm,
        ExperienceDialog,
        ExperienceABTestTypeSelect,
        ExperienceTemplate,
        ExperienceNonABTestList,
        ExperienceTemplateBlank
    }
};
</script>

<script lang="ts" setup>
import { computed, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useCreateDataStore } from '@experience/store/experience.create.store';
import type { ECTemplate } from '@models/ecshop/asset';
import type { ExperienceTemplateV4 } from '@models/experience/useExperienceTemplatesV4';
import analyticsService from '@assets/services/analytics.service';

const { t } = useI18n();
const createDataStore = useCreateDataStore();
const { experienceCreateData, formDialogTitleCode } = storeToRefs(createDataStore);

const visibleBack = computed(() => {
    const { entryComponent, fullscreenDialog } = experienceCreateData.value || {};
    return (
        entryComponent !== fullscreenDialog?.component &&
        fullscreenDialog?.componentData?.visibleBack
    );
});

const dialogTitle = computed(() => {
    switch (experienceCreateData.value.fullscreenDialog?.component) {
        case 'ExperienceCreateEntry':
            return t('experience.common.create_dialog_title');
        case 'ExperienceTemplate':
            return t(
                experienceCreateData.value.fullscreenDialog.componentData?.templateItem
                    ?.templateTitle ||
                    experienceCreateData.value?.entryItem?.templateTitle ||
                    ''
            );
        case 'ExperienceABTestTypeSelect':
            return t('experience.common.create_dialog_title_step2_abtesting');
    }
});

const createDialogIcon = computed(() => {
    const key = experienceCreateData.value?.entryItem?.key || '';
    switch (key) {
        case 'REDIRECT':
            return 'pt-icon-redirect';
        case 'ABTEST':
            return experienceCreateData.value.dialog?.component === 'ExperienceTemplateBlank'
                ? ''
                : 'pt-icon-abtesting1';
        default:
            return '';
    }
});

const createDialogTitle = computed(() => {
    switch (experienceCreateData.value.dialog?.component) {
        case 'ExperienceForm':
            return t(formDialogTitleCode.value);
        case 'ExperienceNonABTestList':
            return t('experience.common.create_dialog_title_step2_advanced');
    }
});

onUnmounted(() => {
    createDataStore.exitExperienceCreate();
});

function handleClose(toRouteInfo: { name?: string; params?: any; query?: any }) {
    const toRoute = toRouteInfo ?? {
        query: {}
    };
    router.push(toRoute);
    createDataStore.exitExperienceCreate();
}

function handleBack(item: 'fullscreenDialog' | 'dialog') {
    const { backComponent, backComponentData } = experienceCreateData.value[item] || {};
    createDataStore.updateExperienceCreateData({
        routePath: 'START',
        [item]: {
            component: backComponent || experienceCreateData.value.entryComponent,
            componentData: backComponentData || {},
            backComponent: '',
            backComponentData: {}
        }
    });
}

function closeDialog() {
    const _fullscreenDialog = experienceCreateData?.value.fullscreenDialog?.component;
    const query = _fullscreenDialog
        ? {
              create: 'START'
          }
        : {};
    router.push({
        query
    });
    createDataStore.updateExperienceCreateData({
        dialog: {}
    });
}

// 空白模板创建
async function handleCreateWithBlank(
    type: 'popup' | 'stickybar',
    pos: string,
    stickyMode: any,
    height: number
) {
    console.log('handleCreateWithBlank', type);
    const experienceType = type === 'popup' ? 'POPUP' : 'STICKY_BAR';
    createDataStore.updateExperienceCreateData({
        experienceType,
        dialog: {
            componentData: {
                blankTemplate: {
                    id: 'blank',
                    editorType: 'popup',
                    engageType: experienceType,
                    position: pos,
                    stickyMode,
                    height,
                    hideShadow:
                        pos === 'pos-sticky-top' && stickyMode === 'inject' ? 'hide' : 'show'
                }
            },
            backComponent: 'ExperienceTemplateBlank',
            backComponentData: {
                visibleBack: false
            }
        }
    });
    analyticsService
        .usePTX('cp_template_create_click', {
            type,
            is_blank: 'Y',
            template_position: 'template_list',
            template_name: ''
        })
        .track();
    await createDataStore.createExperienceHandler();
}

// 从推荐模板列表中选择模板创建
async function handleCreateWithTemplate(params: {
    template: ExperienceTemplateV4 | ECTemplate;
    templateSource: 'COMMON' | 'OWN';
    backTemplateName: any;
}) {
    const { experienceType, entryItem } = createDataStore.getExeperienceTemplateType(
        params.template
    );
    const isInline = experienceType === 'INLINE';
    createDataStore.updateExperienceCreateData({
        experienceType,
        dialog: {
            component: isInline ? 'ExperienceForm' : '',
            componentData: {
                visibleBack: false,
                template: params.template,
                templateSource: params.templateSource || 'COMMON',
                templateLocation: entryItem?.key === 'INLINE' ? 'inline_list' : 'popup_list'
            },
            backComponent: params.backTemplateName
        }
    });
    !isInline && (await createDataStore.createExperienceHandler());
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
</style>
